// LQC-1301 — extracted from SelectPanelIndex so BOTH the Edit Panel and the
// blotter's Trade-Wide Settings popover build the identical PUT /Trade/Execution/Edit
// payload (one source of truth — no drift between the two surfaces).
//
// Behaviour is byte-for-byte the same as the former in-component
// `generateOutgoingTradeMessage` / `determineOrderType` closures: per-CP
// overridable settings fall back to the trade-level value via
// resolveSettingForCP, and the trade-wide fields (releaseCoverSpread,
// autoAffirm, releaseCounterparty) are sent on every CP.

import {
  containsHyphenInQuote,
  returnFractionString,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { ExecutionRequestQuoteLeg } from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import { NotationToDecimalValueType } from "@/lib/interfaces and Enums/TYPENotations";
import tradeSettingsStore, {
  ITradeSettingsStore,
} from "@/store/tradeSettingsStore";
import { EXPIRY_PERIOD_TYPE_SECONDS } from "../../helper/tradeSettingsHelper/expiryDurationHelper";
import {
  resolveSettingForCP,
  returnCounterpartiesAsStringCustomerCodes,
  returnSelectedCounterpartyDesks,
} from "./SelectPanelHelper";

export function determineOrderType(
  data: any,
  thisTradeSettings: ITradeSettingsStore,
) {
  const strategyType: StrategyType = data.strategyType;
  if (!strategyType) {
    return null;
  }
  // Heading: Trade At:
  // Price / Market / Spread
  if (strategyType == StrategyType.Outright) {
    const currentSetting = thisTradeSettings.orderSetLevelOutright.find(
      (obj) => obj.active,
    );
    if (currentSetting?.name == "Market") {
      return "Market";
    }
    // Will not account for difference in Price or Spread
    const currentPriceSetting = thisTradeSettings.orderPriceType.find(
      (obj) => obj.active,
    )?.name;
    return currentPriceSetting; // Limit / Stop
  }
  // Heading: Set level:
  // Spread / Market / Legs
  if (strategyType == StrategyType.Structure) {
    const currentSetting = thisTradeSettings.orderSetLevelStructure.find(
      (obj) => obj.active,
    );
    if (currentSetting?.name == "Market") {
      return "Market";
    }
    // Will not account for difference in Spread or Legs
    const currentPriceSetting = thisTradeSettings.orderPriceType.find(
      (obj) => obj.active,
    )?.name;
    return currentPriceSetting; // Limit / Stop
  }
}

/**
 * Builds the outgoing PUT /Trade/Execution/Edit message for a trade.
 * @param esid            esid used to look up the trade settings in the store.
 * @param data            the ES trade row (provides esid + strategyType for order type).
 * @param currentStoreTrade the live store trade (provides legs + notation per leg).
 * @param notationES      ES-level notation (drives $fr fraction splitting).
 */
export function buildEditPanelMessage({
  esid,
  data,
  currentStoreTrade,
  notationES,
}: {
  esid: string;
  data: any;
  currentStoreTrade: any;
  notationES: NotationToDecimalValueType;
}) {
  const thisTradeSettings = tradeSettingsStore
    .getState()
    .tradeSettingsData.find((setting) => setting.esid == esid)?.settings;

  if (!thisTradeSettings) {
    return;
  }

  const outgoingTradeMessage = {
    //12530 - JSON should have standard Trade Settings for all counterparties across the below fields:
    esid: data.esid,
    // "requestType"
    requestType: thisTradeSettings.tradeTypes.find((obj) => obj.active)?.name,
    // "expiryPeriod" - the store keeps expiryTimeValue as total seconds
    // (LQC-2170), so the period type is always "s".
    expiryPeriod: thisTradeSettings.expiryTimeValue,
    expiryPeriodType: EXPIRY_PERIOD_TYPE_SECONDS,
    // "expiryType"
    expiryType: thisTradeSettings.expiryType.find((obj) => obj.active)?.name,
    // "minimumSize"
    minimumSize: thisTradeSettings.minimumSize,
    // -- COUNTERPARTIES -- //
    // JSON needs to allow for different Trade Settings for each counterparty across the below fields:
    counterparties: returnCounterpartiesAsStringCustomerCodes(
      thisTradeSettings.counterpartiesList,
    ).map((CPCode) => {
      // Resolve per-CP overridable settings (falls back to global if no override)
      const cpQuoteType = resolveSettingForCP(
        thisTradeSettings,
        CPCode,
        "quoteType",
      );
      const cpReleaseDirection = resolveSettingForCP(
        thisTradeSettings,
        CPCode,
        "releaseDirection",
      );
      const cpAutoAffirm = resolveSettingForCP(
        thisTradeSettings,
        CPCode,
        "autoAffirm",
      );
      const cpReleaseCompetition = resolveSettingForCP(
        thisTradeSettings,
        CPCode,
        "releaseCompetition",
      );
      const cpReleaseCover = resolveSettingForCP(
        thisTradeSettings,
        CPCode,
        "releaseCover",
      );
      const cpReleaseCoverSpread = resolveSettingForCP(
        thisTradeSettings,
        CPCode,
        "releaseCoverSpread",
      );
      const cpReleaseSize = resolveSettingForCP(
        thisTradeSettings,
        CPCode,
        "releaseSize",
      );

      return {
        counterparty: CPCode,
        // "level"
        level: thisTradeSettings.level,
        // "releaseCompetition"
        releaseCompetition: cpReleaseCompetition.find((obj) => obj.active)
          ?.booleanValue, // Default as false
        // "releaseCover"
        releaseCover: cpReleaseCover.find((obj) => obj.active)?.booleanValue,
        // "releaseCoverSpread" — per-CP Yes/No boolean (LQC-1705).
        releaseCoverSpread: cpReleaseCoverSpread.find((obj) => obj.active)
          ?.booleanValue,
        releaseCounterparty: thisTradeSettings.releaseCounterparty?.find(
          (obj) => obj.active,
        )?.booleanValue,
        // "quoteType"
        quoteType: cpQuoteType.find((obj) => obj.active)?.name,
        releaseDirection: cpReleaseDirection.find((obj) => obj.active)
          ?.booleanValue, // refers to showing the direction or not
        // "autoAffirm"
        autoAffirm: cpAutoAffirm.find((obj) => obj.active)?.booleanValue,
        // "releaseSize" — UI shows percentage (e.g. 100), API expects decimal (e.g. 1) LQC-623
        releaseSize:
          typeof cpReleaseSize === "number"
            ? cpReleaseSize / 100
            : cpReleaseSize,
        // "releaseAllocation"
        releaseAllocation: thisTradeSettings.releaseAllocation.find(
          (obj) => obj.active,
        )?.booleanValue,
        // "releaseSecurity"
        releaseSecurity: thisTradeSettings.releaseSecurity.find(
          (obj) => obj.active,
        )?.booleanValue,
        deskRestriction: returnSelectedCounterpartyDesks(
          thisTradeSettings.counterpartiesList,
          CPCode,
        ),
        // --- Order Specific ---
        // Order type
        orderType: determineOrderType(data, thisTradeSettings), // "Limit" | "Stop" | "Market"
        // TODO - Can orderType be simplified?
        //referenceSpread - divide by notation multiplier for API
        // $fr: split into whole + fraction if value is a fraction string
        referenceSpread:
          notationES === "$fr" &&
          typeof thisTradeSettings.orderSpreadValue === "string" &&
          containsHyphenInQuote(thisTradeSettings.orderSpreadValue)
            ? returnFractionString(thisTradeSettings.orderSpreadValue, true)
            : (thisTradeSettings.orderSpreadValue ?? undefined),
        referenceSpreadFraction:
          notationES === "$fr" &&
          typeof thisTradeSettings.orderSpreadValue === "string" &&
          containsHyphenInQuote(thisTradeSettings.orderSpreadValue)
            ? returnFractionString(thisTradeSettings.orderSpreadValue, false)
            : undefined,
        // New legs Array
        legs: currentStoreTrade?.legs?.map((leg: ExecutionRequestQuoteLeg) => {
          const refPrice = leg.referencePrice;
          // $fr: split referencePrice into whole + fraction
          // Outrights: notation lives at ES level, not on the leg
          // Structures: each leg has its own notation
          const isOutright =
            currentStoreTrade?.strategyType === StrategyType.Outright;
          const legNotation = isOutright ? notationES : leg.notation;
          if (
            legNotation === "$fr" &&
            typeof refPrice === "string" &&
            containsHyphenInQuote(refPrice)
          ) {
            return {
              blockId: leg.blockId,
              referencePrice: returnFractionString(refPrice, true),
              referencePriceFraction: returnFractionString(refPrice, false),
              referenceAsset: leg.referenceAsset,
              referenceSpread: leg.referenceSpread,
            };
          }
          return {
            blockId: leg.blockId,
            referencePrice: refPrice, // 15658 - No longer / the value.
            referenceAsset: leg.referenceAsset, // Check mapping - Not needed yet
            referenceSpread: leg.referenceSpread, // Check mapping - Not needed yet
          };
        }),
      };
    }),
  };
  return outgoingTradeMessage;
}
